import { FileVariant, IFile } from '@/models/file.model';
import { StepType } from '@/models/step.model';

export enum DocumentsEndpoints {
  DOCUMENTS = '/documents',
  DOCUMENTS_FILES = '/files/documents',
  LEGAL_DOCUMENTS = '/files/legal-documents',
  SELF_DECLARATION = '/files/self-declaration',
  PROPERTIES = '/properties',
}

export const getPropertyDocumentsEndpoints = ({
  propertyId,
  documentId,
  fileId,
}: {
  propertyId?: string;
  fileId?: string;
  documentId?: string;
}) => ({
  PROPERTY_DOCUMENTS: `${DocumentsEndpoints.PROPERTIES}/${propertyId}${DocumentsEndpoints.DOCUMENTS}`,
  PROPERTY_DOCUMENT: `${DocumentsEndpoints.PROPERTIES}/${propertyId}${DocumentsEndpoints.DOCUMENTS}/${documentId}`,
  LEGAL_DOCUMENTS: `${DocumentsEndpoints.PROPERTIES}/${propertyId}${DocumentsEndpoints.LEGAL_DOCUMENTS}`,
  PROPERTY_FILES: `${DocumentsEndpoints.PROPERTIES}/${propertyId}${DocumentsEndpoints.DOCUMENTS_FILES}`,
  PROPERTY_FILE: `${DocumentsEndpoints.PROPERTIES}/${propertyId}${DocumentsEndpoints.DOCUMENTS_FILES}/${fileId}`,
  SELF_DECLARATION: `${DocumentsEndpoints.PROPERTIES}/${propertyId}${DocumentsEndpoints.SELF_DECLARATION}`,
});

export interface IPackageDocumentDTO {
  id: string;
  name_en: string;
  name_no: string;
  description_en: string;
  description_no: string;
  is_required: boolean;
  is_checked: boolean;
}

export enum DocumentUploader {
  USER = 'user',
  ADMIN = 'admin',
  SYSTEM = 'system',
}

export enum DocumentFillingStatus {
  FILLED = 'filled',
  EXPIRED = 'expired',
  UNFILLED = 'unfilled',
}

export interface IPropertyFile {
  PK: string;
  SK: string;
  id: string;
  file_name: string;
  file_link: string;
  user_id: string;
  property_id: string;
  uploadedBy: DocumentUploader;
  created: string;
  modified: string;
  file_type: FileVariant;
}

export interface IDocumentSourceData {
  files: IPropertyFile[];
  documents: IPackageDocumentDTO[];
}

export enum DocumentSourceEnum {
  KARTVERKET = 'KARTVERKET',
  MUNICIPALITY = 'MUNICIPALITY',
  CONDOMINIUM = 'CONDOMINIUM',
  HOUSING_ASSOCIATION = 'HOUSING_ASSOCIATION',
}

export interface IPropertyDocuments {
  [DocumentSourceEnum.KARTVERKET]: IDocumentSourceData;
  [DocumentSourceEnum.CONDOMINIUM]: IDocumentSourceData;
  [DocumentSourceEnum.MUNICIPALITY]: IDocumentSourceData;
  [DocumentSourceEnum.HOUSING_ASSOCIATION]: IDocumentSourceData;
}

export interface IPackageDocumentDTOes {
  [DocumentSourceEnum.KARTVERKET]: IPackageDocumentDTO[];
  [DocumentSourceEnum.CONDOMINIUM]: IPackageDocumentDTO[];
  [DocumentSourceEnum.MUNICIPALITY]: IPackageDocumentDTO[];
  [DocumentSourceEnum.HOUSING_ASSOCIATION]: IPackageDocumentDTO[];
}

export interface IDocumentsStepData extends IUpdateDocumentsStepDTO {
  step_name: StepType.DOCUMENTS;
  documents?: IPropertyDocuments;
  files: IPropertyFile[];
}

export interface ISelfDeclaration {
  PK: string;
  file_name: string;
  file_link: string;
  user_id: string;
  property_id: string;
  uploadedBy: DocumentUploader;
  created: string;
  modified: string;
  file_type: FileVariant;
  expires_at: string;
}

// ------------ DTOs --------------

export interface IUpdateDocumentsStepDTO {
  condition_report_created_at: string;
  rate_report_created_at: string;
  condition_report_includes_rate: boolean;
  price: number;
}

export interface IUploadPropertyFilesDTO {
  files: IFile[];
  type?: FileVariant;
  propertyId: string;
}

export interface IUpdatePropertyDocumentStatusDTO {
  propertyId: string;
  documentId: string;
  is_checked: boolean;
}

export type TGetPropertyFilesDTO = {
  propertyId: string;
};

export type TGetLegalDocumentsDTO = {
  propertyId: string;
};

export type TGetSelfDeclarationDTO = {
  propertyId: string;
};

export type TDeletePropertyFileDTO = {
  propertyId: string;
  fileId: string;
};

export type TGetPropertyDocumentsDTO = {
  propertyId: string;
};

// ------------ Responses --------------

export type TGetPropertyFilesResponse = IPropertyFile[];

export type TGetLegalDocumentsResponse = IPropertyFile[];

export type TGetPropertyDocumentsResponse = IPackageDocumentDTOes;

export type TGetSelfDeclarationResponse = ISelfDeclaration;
